import {
  BarChart3,
  ChefHat,
  Clock,
  Shield,
  Star,
  Store,
  TrendingUp,
  Truck,
  Users,
} from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchPromotionalOffers } from "@/services/landing/hareengEatsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";
import { fetchRestaurantTypes } from "@/services/landing/restaurantService";
import { fetchTestimonials } from "@/services/landing/testimonialsService";

export default function Restaurants() {
  const [restaurantTypes, setRestaurantTypes] = useState<any[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesSlidesPerView, setTypesSlidesPerView] = useState(1);
  const typesSliderRef = useRef<HTMLDivElement>(null);
  const [typesMoreVisible, setTypesMoreVisible] = useState({
    next: false,
    prev: false,
  });
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [offers, setOffers] = useState<any[]>([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchRestaurantTypes().then((types) => {
      setRestaurantTypes(
        Array.isArray(types) ? types.filter((t) => t.hidden === 0) : [],
      );
      setTypesLoading(false);
    });
    fetchTestimonials().then((data) => {
      setTestimonials(
        Array.isArray(data) ? data.filter((t) => t.hidden === 0) : [],
      );
      setTestimonialsLoading(false);
    });
    fetchPromotionalOffers().then((data) => {
      setOffers(Array.isArray(data) ? data.filter((o) => o.isActive) : []);
      setOffersLoading(false);
    });
    fetchHeroBackgrounds("restaurant").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  // Responsive restaurant types slides per view
  useEffect(() => {
    const calcSlides = () => {
      if (window.innerWidth >= 1024) return setTypesSlidesPerView(4);
      if (window.innerWidth >= 640) return setTypesSlidesPerView(2);
      setTypesSlidesPerView(1);
    };
    calcSlides();
    window.addEventListener("resize", calcSlides);
    return () => window.removeEventListener("resize", calcSlides);
  }, []);

  // Track scroll position for types slider arrows
  useEffect(() => {
    const el = typesSliderRef.current;
    if (!el) return;
    const updateArrows = () => {
      setTypesMoreVisible({
        next: el.scrollLeft + el.offsetWidth < el.scrollWidth - 1,
        prev: el.scrollLeft > 0,
      });
    };
    el.addEventListener("scroll", updateArrows);
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [restaurantTypes.length, typesSlidesPerView]);

  // Split restaurant types into grid and slider
  const gridTypes = restaurantTypes.slice(0, typesSlidesPerView);
  const sliderTypes = restaurantTypes.slice(typesSlidesPerView);

  // Scroll handler for types slider
  const scrollTypes = (dir: "left" | "right") => {
    if (!typesSliderRef.current) return;
    const card = typesSliderRef.current.querySelector(".type-card");
    if (!card) return;
    const cardWidth = (card as HTMLElement).offsetWidth + 24;
    typesSliderRef.current.scrollBy({
      behavior: "smooth",
      left: dir === "left" ? -cardWidth : cardWidth,
    });
  };

  {
    /* Promotional Offers */
  }
  <section className="py-16">
    <div className="mx-auto max-w-7xl px-4">
      <div className="mb-12 text-center">
        <h2 className="mb-4 text-4xl font-bold">Promotional Offers</h2>
        <p className="text-xl text-base-content/70">
          Get the latest deals and discounts
        </p>
      </div>
      {offersLoading ? (
        <div className="col-span-3 text-center">Loading...</div>
      ) : offers.length === 0 ? (
        <div className="col-span-3 text-center">No offers found.</div>
      ) : offers.length > 3 ? (
        <Swiper
          breakpoints={{
            640: { slidesPerView: 1 },
            1024: { slidesPerView: 3 },
          }}
          className="!pb-10"
          modules={[Navigation]}
          navigation
          slidesPerView={1}
          spaceBetween={24}
        >
          {offers.map((offer, idx) => (
            <SwiperSlide key={offer.id || idx}>
              <div
                className="card bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                style={{ background: offer.background_color }}
              >
                <figure className="h-40">
                  <img
                    alt={offer.image_alt || offer.title}
                    className="h-full w-full object-cover"
                    src={
                      offer.image_path
                        ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${offer.image_path}`
                        : undefined
                    }
                  />
                </figure>
                <div className="card-body text-white">
                  <h3 className="card-title">{offer.title}</h3>
                  <p className="text-base-content/70">{offer.subtitle}</p>
                  <p className="text-base-content/70">{offer.description}</p>
                  <div className="mt-2 font-bold">
                    Promo Code: {offer.promo_code}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {offers.map((offer, idx) => (
            <div
              className="card bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
              key={offer.id || idx}
              style={{ background: offer.background_color }}
            >
              <figure className="h-40">
                <img
                  alt={offer.image_alt || offer.title}
                  className="h-full w-full object-cover"
                  src={
                    offer.image_path
                      ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${offer.image_path}`
                      : undefined
                  }
                />
              </figure>
              <div className="card-body text-white">
                <h3 className="card-title">{offer.title}</h3>
                <p className="text-base-content/70">{offer.subtitle}</p>
                <p className="text-base-content/70">{offer.description}</p>
                <div className="mt-2 font-bold">
                  Promo Code: {offer.promo_code}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </section>;

  const features = [
    {
      description: "Reach more customers and grow your revenue by up to 30%",
      icon: <TrendingUp className="text-primary" size={32} />,
      title: "Increase Sales",
    },
    {
      description: "Connect with millions of hungry customers in your area",
      icon: <Users className="text-secondary" size={32} />,
      title: "New Customers",
    },
    {
      description:
        "Our delivery partners handle logistics so you can focus on food",
      icon: <Truck className="text-accent" size={32} />,
      title: "Delivery Network",
    },
    {
      description: "Track orders, revenue, and customer feedback in real-time",
      icon: <BarChart3 className="text-success" size={32} />,
      title: "Real-time Analytics",
    },
  ];

  const steps = [
    {
      description: "Create your restaurant profile in minutes",
      icon: <Store className="text-primary" size={32} />,
      step: "1",
      title: "Sign Up",
    },
    {
      description: "Add your dishes with photos and descriptions",
      icon: <ChefHat className="text-secondary" size={32} />,
      step: "2",
      title: "Upload Menu",
    },
    {
      description: "Start receiving orders from hungry customers",
      icon: <Clock className="text-accent" size={32} />,
      step: "3",
      title: "Go Live",
    },
    {
      description: "Use our tools and insights to expand",
      icon: <TrendingUp className="text-success" size={32} />,
      step: "4",
      title: "Grow Business",
    },
  ];

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-secondary-content/20 px-4 py-2">
                  <Store size={20} />
                  <span className="font-medium">
                    Join 10,000+ Partner Restaurants
                  </span>
                </div>

                <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                  Grow your restaurant with{" "}
                  <span className="text-accent">Hareeng Eats</span>
                </h1>

                <p className="mb-8 text-xl opacity-90">
                  Reach more customers, increase sales, and grow your business
                  with Africas fastest-growing food delivery platform.
                </p>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <button className="btn btn-primary btn-lg">
                    <Store size={20} />
                    <a href="/partners/restaurant-registration">
                      Join as a Partner
                    </a>
                  </button>
                </div>
                {/* 
                <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">10K+</div>
                    <div className="text-sm opacity-80">
                      Partner Restaurants
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">1M+</div>
                    <div className="text-sm opacity-80">Monthly Orders</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">30%</div>
                    <div className="text-sm opacity-80">
                      Average Sales Increase
                    </div>
                  </div>
                </div> */}
              </div>

              <div className="relative">
                <div className="rounded-2xl bg-primary-content p-8 text-primary shadow-2xl">
                  <div className="mb-6 text-center">
                    <h3 className="text-2xl font-bold">Earnings Calculator</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Orders per day</span>
                      <span className="font-bold">50</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Average order value</span>
                      <span className="font-bold">R120</span>
                    </div>
                    <div className="divider"></div>
                    <div className="flex items-center justify-between text-lg font-bold">
                      <span>Monthly potential</span>
                      <span className="text-success">R180,000</span>
                    </div>
                  </div>

                  <button className="btn btn-primary mt-6 w-full">
                    <a href="/partners/restaurant-registration">
                      Start Earning
                    </a>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Restaurant Types */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                All Restaurant Types Welcome
              </h2>
              <p className="text-xl text-base-content/70">
                From street food to fine dining - we support every cuisine
              </p>
            </div>

            {typesLoading ? (
              <div className="text-center">Loading...</div>
            ) : restaurantTypes.length === 0 ? (
              <div className="text-center">No restaurant types found.</div>
            ) : (
              <>
                {/* Grid: First typesSlidesPerView items */}
                <div
                  className={`grid grid-cols-1 gap-6 ${
                    typesSlidesPerView >= 2 ? "sm:grid-cols-2" : ""
                  } ${typesSlidesPerView >= 4 ? "lg:grid-cols-4" : ""}`}
                >
                  {gridTypes.map((type, index) => (
                    <div
                      className="type-card card bg-base-100 shadow-lg transition-shadow hover:shadow-xl"
                      key={type.id || index}
                    >
                      <figure className="h-48">
                        <img
                          alt={type.image_alt || type.title}
                          className="h-full w-full object-cover"
                          src={
                            type.image_path
                              ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${type.image_path}`
                              : undefined
                          }
                        />
                      </figure>
                      <div className="card-body">
                        <h3 className="card-title">{type.title}</h3>
                        <p className="text-base-content/70">
                          {type.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Slider: Remaining restaurant types */}
                {sliderTypes.length > 0 && (
                  <div className="mt-8">
                    <div
                      className="flex gap-6 overflow-x-auto scroll-smooth pb-2"
                      ref={typesSliderRef}
                      style={{ scrollbarWidth: "none" }}
                    >
                      {sliderTypes.map((type, index) => (
                        <div
                          className="type-card flex min-w-[280px] max-w-sm flex-shrink-0 flex-col rounded-xl bg-base-100 shadow-md transition-shadow hover:shadow-xl"
                          key={type.id || index}
                        >
                          <figure className="h-48 overflow-hidden rounded-t-xl">
                            <img
                              alt={type.image_alt || type.title}
                              className="h-full w-full object-cover"
                              src={
                                type.image_path
                                  ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${type.image_path}`
                                  : undefined
                              }
                            />
                          </figure>
                          <div className="card-body">
                            <h3 className="card-title">{type.title}</h3>
                            <p className="text-base-content/70">
                              {type.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Arrows below slider */}
                    <div className="mt-4 flex justify-center gap-4">
                      {typesMoreVisible.prev && (
                        <button
                          aria-label="Previous"
                          className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                          onClick={() => scrollTypes("left")}
                          type="button"
                        >
                          <ChevronLeft size={28} />
                        </button>
                      )}
                      {typesMoreVisible.next && (
                        <button
                          aria-label="Next"
                          className="rounded-full bg-base-100 p-2 shadow hover:bg-primary hover:text-white"
                          onClick={() => scrollTypes("right")}
                          type="button"
                        >
                          <ChevronRight size={28} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">How It Works</h2>
              <p className="text-xl text-base-content/70">
                Four simple steps to start growing your restaurant
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {steps.map((step, index) => (
                <div className="text-center" key={index}>
                  <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-content">
                    <span className="text-2xl font-bold">{step.step}</span>
                    <div className="absolute -bottom-2 -right-2 rounded-full bg-base-100 p-2">
                      {step.icon}
                    </div>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
                  <p className="text-base-content/70">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Why Partner With Us?</h2>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div className="card bg-base-100 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{feature.icon}</div>
                    <h3 className="card-title justify-center">
                      {feature.title}
                    </h3>
                    <p className="text-base-content/70">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Success Stories</h2>
              <p className="text-xl text-base-content/70">
                Hear from our restaurant partners
              </p>
            </div>

            {testimonialsLoading ? (
              <div className="col-span-3 text-center">Loading...</div>
            ) : testimonials.length === 0 ? (
              <div className="col-span-3 text-center">
                No active testimonials at the moment.
              </div>
            ) : testimonials.length > 3 ? (
              <Swiper
                breakpoints={{
                  640: { slidesPerView: 1 },
                  1024: { slidesPerView: 3 },
                }}
                className="!pb-10"
                modules={[Navigation]}
                navigation
                slidesPerView={1}
                spaceBetween={24}
              >
                {testimonials.map((t, idx) => (
                  <SwiperSlide key={t.id || idx}>
                    <div className="card bg-base-200 shadow-lg">
                      <div className="card-body">
                        <div className="mb-4 flex items-center gap-4">
                          <img
                            alt={t.avatar_alt || t.name}
                            className="h-12 w-12 rounded-full"
                            src={
                              t.avatar_path
                                ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${t.avatar_path}`
                                : undefined
                            }
                          />
                          <div>
                            <h4 className="font-semibold">{t.name}</h4>
                            <p className="text-sm text-base-content/70">
                              {t.restaurant}
                            </p>
                          </div>
                        </div>
                        <div className="mb-4 flex">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              className={
                                i <= t.rating
                                  ? "fill-current text-warning"
                                  : "text-base-300"
                              }
                              key={i}
                              size={16}
                            />
                          ))}
                        </div>
                        <p className="text-base-content/80">{t.testimonial}</p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="grid gap-8 md:grid-cols-3">
                {testimonials.map((t, idx) => (
                  <div className="card bg-base-200 shadow-lg" key={t.id || idx}>
                    <div className="card-body">
                      <div className="mb-4 flex items-center gap-4">
                        <img
                          alt={t.avatar_alt || t.name}
                          className="h-12 w-12 rounded-full"
                          src={
                            t.avatar_path
                              ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${t.avatar_path}`
                              : undefined
                          }
                        />
                        <div>
                          <h4 className="font-semibold">{t.name}</h4>
                          <p className="text-sm text-base-content/70">
                            {t.restaurant}
                          </p>
                        </div>
                      </div>
                      <div className="mb-4 flex">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star
                            className={
                              i <= t.rating
                                ? "fill-current text-warning"
                                : "text-base-300"
                            }
                            key={i}
                            size={16}
                          />
                        ))}
                      </div>
                      <p className="text-base-content/80">{t.testimonial}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-base-content/70">
                No hidden fees, no monthly charges
              </p>
            </div>

            <div className="card bg-base-100 shadow-2xl">
              <div className="card-body p-8">
                <div className="mb-8 text-center">
                  <div className="text-xl">Commission per order</div>
                  <div className="mt-2 text-base-content/70">
                    Only pay when you earn
                  </div>
                </div>

                <div className="grid gap-8 md:grid-cols-2">
                  <div>
                    <h3 className="mb-4 text-lg font-bold text-success">
                      ✓ What is Included
                    </h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Shield className="text-success" size={16} />
                        <span>Free account setup</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Truck className="text-success" size={16} />
                        <span>Delivery network access</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <BarChart3 className="text-success" size={16} />
                        <span>Real-time analytics</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Users className="text-success" size={16} />
                        <span>Customer support 24/7</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="mb-4 text-lg font-bold">
                      Additional Services
                    </h3>
                    <ul className="space-y-2 text-base-content/70">
                      <li>• Professional food photography</li>
                      <li>• Menu optimization consulting</li>
                      <li>• Marketing campaigns</li>
                      <li>• Priority customer support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
